import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { interviewSchedulingApi } from "../../services/interviewSchedulingApi";
import { formatApiErrorDetail } from "../../lib/api";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

export default function InterviewAvailabilityPage() {
  const { t } = useLocalization();
  const { canManageInterviews } = usePermissions();
  const [settings, setSettings] = useState(null);
  const [blackouts, setBlackouts] = useState([]);
  const [newBlackout, setNewBlackout] = useState({ date: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    interviewSchedulingApi.getSettings().then((r) => setSettings(r.data)).catch(() => {});
    interviewSchedulingApi.listBlackoutDates().then((r) => setBlackouts(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (canManageInterviews) load();
  }, [canManageInterviews]);

  if (!canManageInterviews) return <p className="text-sm text-gray-500" data-testid="interview-availability-access-denied">{t("common.accessDenied")}</p>;
  if (!settings) return <p className="text-sm text-gray-500" data-testid="interview-availability-loading">{t("common.loading")}</p>;

  const toggleDay = (day) => {
    const days = settings.interview_days.includes(day) ? settings.interview_days.filter((d) => d !== day) : [...settings.interview_days, day];
    setSettings({ ...settings, interview_days: days });
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await interviewSchedulingApi.updateSettings({
        interview_days: settings.interview_days,
        window_start: settings.window_start,
        window_end: settings.window_end,
        interview_duration_minutes: Number(settings.interview_duration_minutes),
        protected_block_minutes: Number(settings.protected_block_minutes),
        slot_increment_minutes: Number(settings.slot_increment_minutes),
        min_advance_notice_hours: Number(settings.min_advance_notice_hours),
        scheduling_horizon_days: Number(settings.scheduling_horizon_days),
      });
      setSettings(data);
      toast.success(t("mgmtRecruitment.settingsUpdated"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addBlackout = async () => {
    if (!newBlackout.date || !newBlackout.reason.trim()) {
      toast.error(t("mgmtRecruitment.dateReasonRequired"));
      return;
    }
    try {
      await interviewSchedulingApi.addBlackoutDate(newBlackout);
      setNewBlackout({ date: "", reason: "" });
      toast.success(t("mgmtRecruitment.blackoutAdded"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeBlackout = async (id) => {
    try {
      await interviewSchedulingApi.removeBlackoutDate(id);
      toast.success(t("mgmtRecruitment.blackoutRemoved"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="interview-availability-page">
      <PageHelpBanner pageKey="interview-availability" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtRecruitment.availabilitySettingsHelp")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtRecruitment.interviewAvailabilityTitle")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtRecruitment.interviewAvailabilitySubtitle")}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4" data-testid="interview-settings-form">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">{t("mgmtRecruitment.availableInterviewDays")}</p>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((day) => (
              <button
                key={day}
                type="button"
                data-testid={`settings-day-${day}`}
                onClick={() => toggleDay(day)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${settings.interview_days.includes(day) ? "bg-blue-950 text-white border-blue-950" : "bg-white text-gray-500 border-gray-300"}`}
              >
                {day.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t("mgmtRecruitment.windowStart")}</label>
            <input data-testid="settings-window-start" type="time" value={settings.window_start} onChange={(e) => setSettings({ ...settings, window_start: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t("mgmtRecruitment.windowEnd")}</label>
            <input data-testid="settings-window-end" type="time" value={settings.window_end} onChange={(e) => setSettings({ ...settings, window_end: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t("mgmtRecruitment.interviewLengthMin")}</label>
            <input data-testid="settings-duration" type="number" step={5} value={settings.interview_duration_minutes} onChange={(e) => setSettings({ ...settings, interview_duration_minutes: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t("mgmtRecruitment.protectedBlockMin")}</label>
            <input data-testid="settings-block-minutes" type="number" step={30} value={settings.protected_block_minutes} onChange={(e) => setSettings({ ...settings, protected_block_minutes: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t("mgmtRecruitment.minAdvanceNoticeHrs")}</label>
            <input data-testid="settings-advance-notice" type="number" value={settings.min_advance_notice_hours} onChange={(e) => setSettings({ ...settings, min_advance_notice_hours: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t("mgmtRecruitment.schedulingHorizonDays")}</label>
            <input data-testid="settings-horizon-days" type="number" value={settings.scheduling_horizon_days} onChange={(e) => setSettings({ ...settings, scheduling_horizon_days: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </div>
        </div>

        <button data-testid="save-interview-settings-button" onClick={saveSettings} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-950 text-white text-sm font-semibold hover:bg-blue-900 disabled:opacity-50">
          <Save size={14} /> {saving ? t("mgmtRecruitment.saving") : t("common.save")}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="blackout-dates-form">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{t("mgmtRecruitment.blackoutDates")}</p>
        <div className="flex flex-wrap gap-2">
          <input data-testid="new-blackout-date-input" type="date" value={newBlackout.date} onChange={(e) => setNewBlackout((f) => ({ ...f, date: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          <input data-testid="new-blackout-reason-input" placeholder={t("mgmtRecruitment.reason")} value={newBlackout.reason} onChange={(e) => setNewBlackout((f) => ({ ...f, reason: e.target.value }))} className="flex-1 min-w-[160px] px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          <button data-testid="add-blackout-date-button" onClick={addBlackout} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 text-blue-950 text-sm font-semibold hover:bg-amber-300">
            <Plus size={14} /> {t("mgmtRecruitment.add")}
          </button>
        </div>

        <div className="divide-y divide-gray-100">
          {blackouts.map((b) => (
            <div key={b.id} className="flex items-center justify-between py-2" data-testid={`blackout-row-${b.id}`}>
              <div className="text-sm">
                <span className="font-semibold text-gray-800">{b.date}</span>
                <span className="text-gray-500 ml-2">{b.reason}</span>
              </div>
              <button data-testid={`remove-blackout-${b.id}`} onClick={() => removeBlackout(b.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
            </div>
          ))}
          {blackouts.length === 0 && <p className="text-sm text-gray-500 py-2" data-testid="no-blackout-dates">{t("mgmtRecruitment.noBlackoutDates")}</p>}
        </div>
      </div>
    </div>
  );
}
